import * as React from 'react';

import { HonestWork, PopupDirection } from '@cian/honestwork-component';
import { Location } from 'history';

import { Answers } from './answers';
import { CommentForm } from './comment_form';
import { IComment, ICommentState } from '../../types/posts';
import { IUser } from '../../types/user';
import { formatDateFull } from '../../../app/helpers/format_helper';
import { canEditDelete } from '../../../app/helpers/comments_helper';
import { Likes } from '../../containers/likes/likes';
import { permissionsFull, permissionsExtend } from './permissions';
import { getCleanText } from '../../../app/helpers/make_html_helper';
import { avatar } from './avatar';

import s from './comments.css';

interface IProps {
  location: Location;
  canComment: boolean;
  user: IUser;
  likesShown: boolean;
  author?: IUser;
  restrictComments: boolean;
  commentsState: ICommentState;
  comment: IComment;
  onCommentEdit(id: number, comment: string, email: string): void;
  onCommentDelete(id: number, email: string): void;
  onCommentHide(id: number, email: string): void;
  onCommentSubmit(
    comment: string,
    userId: number,
    fullName: string,
    email: string,
    parentID: number | null,
    subs: boolean,
  ): void;
  cardType: string;
}

export const Comment: React.FC<IProps> = ({
  location,
  canComment,
  user,
  likesShown,
  author,
  restrictComments,
  commentsState,
  comment,
  onCommentEdit,
  onCommentDelete,
  onCommentHide,
  onCommentSubmit,
  cardType,
}) => {
  const [addingComment, setAddingComment] = React.useState(false);
  const [replyTo, setReplyTo] = React.useState('');
  const [parentID, setParentID] = React.useState<number | null>(null);
  const [editingComment, setEditingComment] = React.useState(false);
  const [likesCount, setLikesCount] = React.useState(comment.likesCount || 0);
  const [dislikesCount, setDislikesCount] = React.useState(comment.dislikesCount || 0);
  const [, setCurrentUserLike] = React.useState(false);
  const [, setCurrentUserDislike] = React.useState(false);
  const [userLike, setUserLike] = React.useState<1 | -1 | null>(comment.userLike || null);
  const [savedFields, setSavedFields] = React.useState<{
    comment: string;
    name: string;
    email: string;
    subscribe: boolean;
  } | undefined>(undefined);

  const prevLoadedRef = React.useRef(commentsState.loaded);

  React.useEffect(() => {
    const wasLoaded = prevLoadedRef.current;
    prevLoadedRef.current = commentsState.loaded;

    if (!commentsState.error && commentsState.loaded && commentsState.loaded !== wasLoaded) {
      setAddingComment(false);
      setReplyTo('');
      setParentID(null);
      setEditingComment(false);
    }
  }, [commentsState.error, commentsState.loaded]);

  const canCommentFn = () => {
    return (
      !restrictComments ||
      (
        !!author && !!comment.user &&
        (
          author.userId === user.userId ||
          comment.user.userId === user.userId
        )
      )
    );
  };

  const isNew = () => {
    const query = new URLSearchParams(location.search);
    return (
      query.get('ts') &&
      parseInt(query.get('ts') as string) ||
      new Date().getTime()
    ) - 2 * 60 * 60 * 1000 < new Date(comment.datePublish).getTime();
  };

  const isSelected = () => {
    const hash = location.hash;
    return hash && Number(hash.replace('#', '')) === comment.id;
  };

  const isAuthor = user && comment.user && user.userId === comment.user.userId;
  const isCanEdit = isAuthor && canEditDelete(comment.datePublish) && !editingComment;
  const isCanDelete = isAuthor && canEditDelete(comment.datePublish) ||
    (cardType === 'Question' && user.roles.includes(permissionsFull));
  const isCanHide = user && (user.roles.includes(permissionsFull) || user.roles.includes(permissionsExtend));

  const handleReply = () => {
    if (user.userId === -1) {
      window.waitForLoginPopup();
      return;
    }

    setAddingComment(true);
    setReplyTo(
      comment.user && comment.user.userId > 0
        ? (comment.user.fullName || comment.user.companyName || `ID: ${comment.user.userId}`)
        : 'Аноним',
    );
    setParentID(comment.id);
    setEditingComment(false);
  };

  const handleCommentSubmit = (
    commentText: string,
    userId: number,
    fullName: string,
    email: string,
    pid: number | null,
    subs: boolean,
  ) => onCommentSubmit(commentText, userId, fullName, email, pid, subs);

  const saveFields = (commentText: string, name: string, email: string, subscribe: boolean) => {
    setSavedFields({ comment: commentText, name, email, subscribe });
  };

  const getUserName = () => {
    const name = comment.user && comment.user.userId > 0
      ? (comment.user.fullName || comment.user.companyName || `ID: ${comment.user.userId}`)
      : 'Аноним';

    if (comment.user?.specialistLink) {
      return <a className={s['comments__item_name-text']} href={`${comment.user.specialistLink}`}>{name}</a>;
    }

    return <span className={s['comments__item_name-text']}>{name}</span>;
  };

  return (
    <div className={s['comments__list_item']}>
      <div
        id={`${comment.id}`}
        className={`
          ${s['comments__list_wrap-comment']}
          ${isNew() || isSelected() ? s['comments__list_item-new'] : ''}`}
      >
        <div className={s['comments__item_wrap-info']}>
          {comment.user && avatar(comment.user)}
          {!comment.user && <div className={s['comments__form_photo-default']} />}
          <div className={s['comments__item_info']}>
            <span className={s['comments__item_name']}>
              {getUserName()}
              {comment.user && comment.bestAdvice &&
                <span className={s['comments__item_best-advice']}>
                  Лучший совет
                </span>
              }
              {comment.user && comment.user.userTrust &&
                <HonestWork
                  direction={PopupDirection.RIGHT}
                  className={s['comments__item_honest']}
                />
              }
            </span>
            <span className={s['comments__item_date']}>
              {formatDateFull(comment.datePublish)}
            </span>
          </div>
          {likesShown &&
            <div className={s['comments__likes-wrap-dekstop']}>
              <Likes
                disabled={!canComment ||
                  (comment.user && user.userId === comment.user.userId) ||
                  user.isBlock}
                isCompact={true}
                likesCount={likesCount}
                dislikesCount={dislikesCount}
                userLike={userLike}
                onLikesUpdate={(likes: number, dislikes: number, newUserLike: 1 | -1 | null) => {
                  setLikesCount(likes);
                  setCurrentUserLike(false);
                  setCurrentUserDislike(false);
                  setDislikesCount(dislikes);
                  setUserLike(newUserLike);
                }}
                type="threadedcomment"
                objectID={`${comment.id}`}
              />
            </div>
          }
        </div>
        <p
          dangerouslySetInnerHTML={{ __html: getCleanText(comment.comment) }}
          className={s['comments__item_text']}
        />
        <div className={s['comments__item_bottom-line']}>
          {likesShown &&
            <div className={s['comments__likes-wrap-mobile']}>
              <Likes
                disabled={!canComment ||
                  (comment.user && user.userId === comment.user.userId) ||
                  user.isBlock}
                isCompact={true}
                likesCount={likesCount}
                dislikesCount={dislikesCount}
                userLike={userLike}
                onLikesUpdate={(likes: number, dislikes: number, newUserLike: 1 | -1 | null) => {
                  setLikesCount(likes);
                  setCurrentUserLike(false);
                  setCurrentUserDislike(false);
                  setDislikesCount(dislikes);
                  setUserLike(newUserLike);
                }}
                type="threadedcomment"
                objectID={`${comment.id}`}
              />
            </div>
          }
          {!addingComment && canCommentFn() && canComment &&
            <span
              className={s['comments__item_bottom-line-item']}
              onClick={handleReply}
            >
              Ответить
            </span>
          }
          {isCanEdit &&
            <span
              className={s['comments__item_bottom-line-item']}
              onClick={() => {
                setAddingComment(false);
                setReplyTo(
                  comment.user && comment.user.userId > 0
                    ? (comment.user.fullName || comment.user.companyName || `ID: ${comment.user.userId}`)
                    : 'Аноним',
                );
                setParentID(comment.id);
                setEditingComment(true);
              }}
            >
              Редактировать
            </span>
          }
          {isCanDelete &&
            <span
              className={s['comments__item_bottom-line-item']}
              onClick={() => onCommentDelete(comment.id, comment.user.email)}
            >
              Удалить
            </span>
          }
          {isCanHide &&
            <span
              className={s['comments__item_bottom-line-item']}
              onClick={() => onCommentHide(comment.id, comment.user.email)}
            >
              Скрыть
            </span>
          }
        </div>
        {canComment && (addingComment || editingComment) &&
          <CommentForm
            onOpen={() => {}}
            onClose={() => {
              setAddingComment(false);
              setReplyTo('');
              setParentID(null);
              setEditingComment(false);
            }}
            onCommentSubmit={handleCommentSubmit}
            commentsState={commentsState}
            replyTo={replyTo}
            parentID={parentID}
            editingComment={editingComment}
            onCommentEdit={onCommentEdit}
            comment={comment}
            isOpened={true}
            user={user}
            saveFields={saveFields}
            savedFields={savedFields}
            cardType={cardType}
          />
        }
      </div>
      {comment.comments.length > 0 &&
        <Answers
          location={location}
          likesShown={likesShown}
          canComment={canComment && canCommentFn()}
          replyTo={
            comment.user && comment.user.userId > 0
              ? (comment.user.fullName || `ID: ${comment.user.userId}`)
              : 'Аноним'
          }
          parentID={comment.id}
          onCommentSubmit={handleCommentSubmit}
          commentsState={commentsState}
          user={user}
          answers={comment.comments}
          onCommentEdit={onCommentEdit}
          onCommentDelete={onCommentDelete}
          onCommentHide={onCommentHide}
          cardType={cardType}
        />
      }
    </div>
  );
};
